
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from "../context/AuthContext";
import { ForgotPasswordModal } from "./auth/ForgotPasswordModal";
import { api } from "../services/api";
import type { AuthUser } from "../types/authUser";
import { lightTheme } from "../styles/theme";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MailOutlined from "@mui/icons-material/MailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  FormSection,
  FormContainer,
  FormTitle,
  FormBrand,
  FormBrandLockup,
  VisuallyHidden,
  InputGroup,
  Label,
  Input,
  SubmitButton,
  Message,
  ToggleButton,
  RecaptchaContainer,
  ForgotPasswordLink,
  PasswordInputWrapper,
  PasswordToggleButton,
  InputWithIconWrapper,
  InputLeftIcon,
  OrDivider,
  SignUpHighlight,
} from "./auth/DashboardAuthStyles";
import { Chip, Box, Typography } from "@mui/material";
import {
  getLastLoginType,
  setLastLoginType,
  type DashboardLoginType,
} from "../utils/lastLoginType";
import { buildProfileIdPreview } from "../domain/auth/profileId";
import {
  getPasswordRequirements,
  validatePassword,
} from "../domain/auth/passwordRules";
import {
  ADMIN_LOGIN_ERROR_MESSAGE,
  resolveAdminLoginErrorMessage,
} from "../domain/auth/adminLoginErrors";
import { transformTeamMemberToAdminStructure } from "../domain/auth/transformTeamMember";

interface SchoolSignupFormProps {
  initialMode?: "signup" | "login";
}

const BUSINESS_TYPE_OPTIONS = [
  { value: "organization", label: "Organization" },
  { value: "school", label: "School" },
  { value: "church", label: "Church" },
  { value: "individual", label: "Individual" },
] as const;
const BUSINESS_TYPE_VALUES = BUSINESS_TYPE_OPTIONS.map((o) => o.value);
const LOGIN_TYPE_VALUES: readonly DashboardLoginType[] = ["admin", "team"];

// WAI-ARIA radiogroup pattern: arrow keys move both selection and focus
// (roving tabindex) between the Chip options rendered with role="radio".
function handleRadioGroupArrowKeys<T extends string>(
  e: React.KeyboardEvent,
  options: readonly T[],
  currentValue: T,
  onSelect: (value: T) => void,
  refs: React.MutableRefObject<(HTMLElement | null)[]>
) {
  let step = 0;
  if (e.key === "ArrowRight" || e.key === "ArrowDown") step = 1;
  else if (e.key === "ArrowLeft" || e.key === "ArrowUp") step = -1;
  else return;

  e.preventDefault();
  const currentIndex = options.indexOf(currentValue);
  const nextIndex = (currentIndex + step + options.length) % options.length;
  onSelect(options[nextIndex]);
  refs.current[nextIndex]?.focus();
}

const SchoolSignupForm: React.FC<SchoolSignupFormProps> = ({
  initialMode = "signup",
}) => {
  const [email, setEmail] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState<"signup" | "login" | "verify">(initialMode);
  const [showResendLink, setShowResendLink] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);
  const [showTeamForgotPasswordMessage, setShowTeamForgotPasswordMessage] =
    useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [businessType, setBusinessType] = useState<"organization" | "school" | "church" | "individual">("school");
  const businessTypeChipRefs = useRef<(HTMLElement | null)[]>([]);

  // Team member login fields
  const [loginType, setLoginType] = useState<DashboardLoginType>(getLastLoginType);
  const loginTypeChipRefs = useRef<(HTMLElement | null)[]>([]);
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Move focus to the new heading when the form swaps between signup/login/
  // verify — otherwise keyboard/screen-reader users get no indication that
  // the entire form was replaced. Skips the very first render so mounting
  // doesn't steal focus from wherever the user naturally landed.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [mode]);

  const selectLoginType = (type: DashboardLoginType) => {
    setLoginType(type);
    setLastLoginType(type);
  };

  // Using API service instead of direct fetch calls

  useEffect(() => {
    if (mode !== "signup") {
      setConfirmPasswordError("");
      return;
    }

    if (!confirmPassword) {
      setConfirmPasswordError("");
      return;
    }

    setConfirmPasswordError(
      password === confirmPassword ? "" : "Passwords do not match"
    );
  }, [password, confirmPassword, mode]);

  useEffect(() => {
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowPin(false);
  }, [mode, loginType]);

  useEffect(() => {
    if (mode !== "login") return;

    const params = new URLSearchParams(location.search);
    const tab = (params.get("tab") || params.get("loginType") || "").toLowerCase();

    if (tab === "team" || tab === "teacher") {
      selectLoginType("team");
      return;
    }

    if (
      tab === "org" ||
      tab === "organization" ||
      tab === "admin" ||
      tab === "school" ||
      tab === "church"
    ) {
      selectLoginType("admin");
    }
  }, [location.search, mode]);

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setMessage("Please enter the verification code.");
      return;
    }

    setIsVerifying(true);
    setMessage("");

    try {
      await api.auth.verifyAccount(signupEmail, verificationCode.trim());
      setMessage("Account verified successfully! You can now login.");
      setMode("login");
      setVerificationCode("");
      setSignupEmail("");
    } catch (err) {
      console.error("Verification error:", err);
      setMessage("Verification failed. Please check your code and try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendVerification = async () => {
    if (!signupEmail.trim()) {
      setMessage("Please enter your email address first.");
      return;
    }

    setIsResending(true);
    try {
      await api.auth.resendVerification(signupEmail.trim());
      setMessage(
        "Verification email sent successfully! Please check your inbox and enter the code above."
      );
    } catch (err) {
      console.error("Resend verification error:", err);
      setMessage("Failed to resend verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleSignup = async (
    trimmedEmail: string,
    trimmedPassword: string,
    trimmedSchoolName: string,
    trimmedConfirmPassword: string
  ) => {
    if (
      !trimmedEmail ||
      !trimmedPassword ||
      !trimmedSchoolName ||
      !trimmedConfirmPassword
    ) {
      setMessage("Please fill in all fields.");
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    const passwordValidation = validatePassword(trimmedPassword);
    if (!passwordValidation.isValid) {
      setMessage(passwordValidation.messages.join(". "));
      return;
    }

    if (!recaptchaToken) {
      setMessage("Please complete the CAPTCHA.");
      return;
    }

    setIsSigningUp(true);
    try {
      const data = await api.auth.signup(
        trimmedSchoolName,
        trimmedEmail,
        trimmedPassword,
        businessType
      );

      if (data.message === "Organization account created successfully") {
        setMessage(
          "Signup successful! Please check your email for verification instructions."
        );
        setSignupEmail(trimmedEmail);
        setMode("verify");
        setEmail("");
        setPassword("");
        setSchoolName("");
        setConfirmPassword("");
        setRecaptchaToken("");
      } else {
        setMessage(data.message || "Signup failed");
      }
    } catch (err) {
      console.error("Signup error:", err);
      setMessage("Signup failed");
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleAdminLogin = async (
    trimmedEmail: string,
    trimmedPassword: string
  ) => {
    if (!trimmedEmail || !trimmedPassword) {
      setMessage("Email and password are required.");
      return;
    }

    try {
      setIsLoggingIn(true);
      const data = await api.auth.login(trimmedEmail, trimmedPassword);

      if ("error" in data) {
        if (data.error === "Account not verified") {
          setMessage(
            "Account not verified. Please verify your account first."
          );
          setSignupEmail(trimmedEmail);
          setMode("verify");
          setEmail("");
          setPassword("");
        } else {
          setMessage(resolveAdminLoginErrorMessage(data));
        }
      } else if (data.message === "Login successful" && data.tokens) {
        const org = data.organization;
        const school = data.school;

        setLastLoginType("admin");
        const userPayload: AuthUser = {
          id: org?.oid || school?.schoolId || "",
          email: org?.email || school?.email || "",
          schoolId: org?.oid || school?.schoolId || "",
          soid: org?.soid,
          schoolName: org?.name || school?.schoolName || "",
          schoolLogo: org?.logo || school?.schoolLogo || "",
          schoolDescription:
            org?.description || school?.schoolDescription || "",
          schoolType: org?.type || school?.schoolType || "",
          isSchoolApproved:
            org?.isApproved !== undefined
              ? org.isApproved
              : school?.isSchoolApproved !== undefined
              ? school.isSchoolApproved
              : false,
          tier: org?.tier || school?.tier || "0",
          sponsored: org?.sponsored === true || school?.sponsored === true,
          isCircleOpen: org?.isPublic || school?.isCircleOpen || "false",
          isPublic: org?.isPublic || school?.isCircleOpen || "false",
          createdAt: org?.createdAt || school?.createdAt || "",
          PK: org?.PK || school?.PK || "",
          SK: org?.SK || school?.SK || "",
          orgBanner: org?.orgBanner || org?.banner || undefined,
          phoneNumber: org?.phoneNumber || undefined,
          website: org?.website || undefined,
          featureFlags: org?.featureFlags || undefined,
          publicPageSettings: org?.publicPageSettings || undefined,
          notificationSettings: org?.notificationSettings || undefined,
          userType: "admin",
        };
        login(userPayload, data.tokens.idToken, data.tokens.refreshToken);

        navigate("/");
      } else {
        setMessage(data.message || ADMIN_LOGIN_ERROR_MESSAGE);

        if (data.message?.includes("pending approval")) {
          setShowResendLink(true);
        } else {
          setShowResendLink(false);
        }
      }
    } catch {
      setMessage(ADMIN_LOGIN_ERROR_MESSAGE);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleTeamLogin = async () => {
    const trimmedUsername = username.trim();
    const trimmedPin = pin.trim();

    if (!trimmedUsername || !trimmedPin) {
      setMessage("Username and PIN are required.");
      return;
    }

    try {
      setIsLoggingIn(true);
      const data = await api.auth.teamLogin(trimmedUsername, trimmedPin);

      if ("error" in data) {
        setMessage(data.error || "Login failed");
      } else if (data.success && data.token) {
        const transformedData = transformTeamMemberToAdminStructure(data.user);
        setLastLoginType("team");
        login(transformedData, data.token);
        navigate("/");
      } else {
        setMessage("Login failed. Please check your credentials.");
      }
    } catch {
      setMessage("Login failed. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setConfirmPasswordError("");
    setShowResendLink(false);

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const trimmedSchoolName = schoolName.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (mode === "signup") {
      await handleSignup(
        trimmedEmail,
        trimmedPassword,
        trimmedSchoolName,
        trimmedConfirmPassword
      );
      return;
    }

    if (mode === "login") {
      if (loginType === "admin") {
        await handleAdminLogin(trimmedEmail, trimmedPassword);
      } else {
        await handleTeamLogin();
      }
    }
  };

  const profileIdPreview = buildProfileIdPreview(schoolName);

  return (
    <FormSection>
      {/* Main Form - Only show for signup and login */}
      {(mode === "signup" || mode === "login") && (
        <FormContainer>
          {mode === "signup" ? (
            <>
              <FormTitle ref={headingRef} tabIndex={-1}>Create an Account</FormTitle>
              <ToggleButton
                type="button"
                onClick={() => {
                  setMode("login");
                  setMessage("");
                }}
                style={{ fontSize: "0.75rem", margin: "-0.75rem auto 1rem" }}
              >
                ← Back to login
              </ToggleButton>
            </>
          ) : (
            <FormBrand>
              <VisuallyHidden ref={headingRef} tabIndex={-1}>Login</VisuallyHidden>
              <FormBrandLockup
                src="/brand/noticeboard-logo-lockup.webp"
                alt="Noticeboard"
              />
            </FormBrand>
          )}
          <form onSubmit={handleSubmit}>
            {mode === "signup" && (
              <>
                <InputGroup>
                  <Label htmlFor="schoolName">Name</Label>
                  <Input
                    id="schoolName"
                    type="text"
                    placeholder="Enter your organization name"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    required
                    autoComplete="organization"
                    data-testid="signup-school-name-input"
                  />
                {schoolName.trim() && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: { xs: 1.5, sm: 2 },
                      backgroundColor: lightTheme.colors.white,
                      borderRadius: 1,
                      border: `1px solid ${lightTheme.colors.buttonHover}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      }}
                    >
                      Your profile id
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="text.primary"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontSize: { xs: "0.9375rem", sm: "1rem" },
                      }}
                    >
                      @{profileIdPreview}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        fontSize: { xs: "0.72rem", sm: "0.75rem" },
                        lineHeight: 1.3,
                      }}
                    >
                      NB: This cannot be changed once created
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        fontSize: { xs: "0.72rem", sm: "0.75rem" },
                        lineHeight: 1.3,
                        mt: 0.5,
                      }}
                    >
                      If this ID is already taken, we will append a number (for
                      example, @{profileIdPreview}2).
                    </Typography>
                    {schoolName.trim().length > 25 && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{
                          display: "block",
                          fontSize: { xs: "0.72rem", sm: "0.75rem" },
                          mt: 1,
                        }}
                      >
                        Organization name is longer than 25 characters. Consider
                        using a shorter, more concise name for better team
                        member usernames.
                      </Typography>
                    )}
                  </Box>
                )}
              </InputGroup>

              <InputGroup>
                <Label id="signup-business-type-label">Type</Label>
                <Box
                  role="radiogroup"
                  aria-labelledby="signup-business-type-label"
                  onKeyDown={(e) =>
                    handleRadioGroupArrowKeys(
                      e,
                      BUSINESS_TYPE_VALUES,
                      businessType,
                      setBusinessType,
                      businessTypeChipRefs
                    )
                  }
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                >
                  {BUSINESS_TYPE_OPTIONS.map((option, index) => (
                    <Chip
                      key={option.value}
                      ref={(el: HTMLElement | null) => {
                        businessTypeChipRefs.current[index] = el;
                      }}
                      label={option.label}
                      clickable
                      role="radio"
                      aria-checked={businessType === option.value}
                      tabIndex={businessType === option.value ? 0 : -1}
                      variant={businessType === option.value ? "filled" : "outlined"}
                      color={businessType === option.value ? "primary" : "default"}
                      onClick={() => setBusinessType(option.value)}
                      sx={{ fontSize: (theme) => theme.typography.body2.fontSize }}
                      data-testid={`signup-business-type-${option.value}`}
                    />
                  ))}
                </Box>
              </InputGroup>
              </>
            )}

            {mode === "login" && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <Typography
                  id="login-type-label"
                  variant="body2"
                  sx={{ mb: 0.75, color: "text.secondary" }}
                >
                  Login as:
                </Typography>
                <Box
                  role="radiogroup"
                  aria-labelledby="login-type-label"
                  onKeyDown={(e) =>
                    handleRadioGroupArrowKeys(
                      e,
                      LOGIN_TYPE_VALUES,
                      loginType,
                      selectLoginType,
                      loginTypeChipRefs
                    )
                  }
                  sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                >
                  <Chip
                    ref={(el: HTMLElement | null) => {
                      loginTypeChipRefs.current[0] = el;
                    }}
                    label="Organization Admin"
                    clickable
                    role="radio"
                    aria-checked={loginType === "admin"}
                    tabIndex={loginType === "admin" ? 0 : -1}
                    variant={loginType === "admin" ? "filled" : "outlined"}
                    color={loginType === "admin" ? "primary" : "default"}
                    onClick={() => selectLoginType("admin")}
                    sx={{
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      height: { xs: 36, md: 32 },
                      borderRadius: "999px",
                      px: { xs: 0.5, md: 0 },
                    }}
                    data-testid="login-type-admin"
                  />
                  <Chip
                    ref={(el: HTMLElement | null) => {
                      loginTypeChipRefs.current[1] = el;
                    }}
                    label="Team Member"
                    clickable
                    role="radio"
                    aria-checked={loginType === "team"}
                    tabIndex={loginType === "team" ? 0 : -1}
                    variant={loginType === "team" ? "filled" : "outlined"}
                    color={loginType === "team" ? "primary" : "default"}
                    onClick={() => selectLoginType("team")}
                    sx={{
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      height: { xs: 36, md: 32 },
                      borderRadius: "999px",
                      px: { xs: 0.5, md: 0 },
                    }}
                    data-testid="login-type-team"
                  />
                </Box>
              </Box>
            )}

            {mode === "login" && loginType === "team" ? (
              // Team member login fields
              <>
                <InputGroup>
                  <Label htmlFor="username">Username or Phone Number</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="e.g., admin@noticeboard or 0821234567"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                    data-testid="login-team-username-input"
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="pin">PIN</Label>
                  <PasswordInputWrapper>
                    <Input
                      id="pin"
                      type={showPin ? "text" : "password"}
                      placeholder="Enter your 4-digit PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      maxLength={4}
                      required
                      autoComplete="off"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      data-testid="login-team-pin-input"
                    />
                    <PasswordToggleButton
                      type="button"
                      onClick={() => setShowPin((prev) => !prev)}
                      aria-label={showPin ? "Hide PIN" : "Show PIN"}
                    >
                      {showPin ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </PasswordToggleButton>
                  </PasswordInputWrapper>
                  {mode === "login" && (
                    <ForgotPasswordLink
                      onClick={() => setShowTeamForgotPasswordMessage(true)}
                    >
                      Forgot PIN?
                    </ForgotPasswordLink>
                  )}
                  {showTeamForgotPasswordMessage && (
                    <Message isError={false} role="status">
                      Contact your organization administrator/ noticeboard
                      customer agent to reset your password.
                    </Message>
                  )}
                </InputGroup>
              </>
            ) : (
              // Organization admin login/signup fields
              <>
                <InputGroup>
                  <Label htmlFor="email">
                    {mode === "signup" ? "Admin Email" : "Email"}
                  </Label>
                  <InputWithIconWrapper>
                    <InputLeftIcon>
                      <MailOutlined />
                    </InputLeftIcon>
                    <Input
                      id="email"
                      type="email"
                      placeholder={
                        mode === "signup" ? "Enter admin email" : "Enter email"
                      }
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete={mode === "signup" ? "email" : "username"}
                      $hasLeadingIcon
                      data-testid={mode === "signup" ? "signup-email-input" : "login-email-input"}
                    />
                  </InputWithIconWrapper>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="password">Password</Label>
                  <PasswordInputWrapper>
                    <InputLeftIcon>
                      <LockOutlined />
                    </InputLeftIcon>
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={
                        mode === "signup" ? "Create password" : "Enter password"
                      }
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete={
                        mode === "signup" ? "new-password" : "current-password"
                      }
                      aria-describedby={
                        mode === "signup" && password
                          ? "password-requirements"
                          : undefined
                      }
                      $hasLeadingIcon
                      data-testid={mode === "signup" ? "signup-password-input" : "login-password-input"}
                    />
                    <PasswordToggleButton
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </PasswordToggleButton>
                  </PasswordInputWrapper>
                  {mode === "signup" && password && (
                    <Box id="password-requirements" aria-live="polite" sx={{ mt: 1 }}>
                      {getPasswordRequirements(password).map((err) => (
                        <Typography
                          key={err}
                          variant="caption"
                          color="error"
                          display="block"
                        >
                          • {err}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {mode === "login" && (
                    <ForgotPasswordLink
                      onClick={() => setIsForgotPasswordModalOpen(true)}
                    >
                      Forgot Password?
                    </ForgotPasswordLink>
                  )}
                </InputGroup>
              </>
            )}

            {mode === "signup" && (
              <InputGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <PasswordInputWrapper>
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    aria-describedby={
                      confirmPassword && confirmPasswordError
                        ? "confirm-password-error"
                        : undefined
                    }
                    data-testid="signup-confirm-password-input"
                  />
                  <PasswordToggleButton
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                  >
                    {showConfirmPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </PasswordToggleButton>
                </PasswordInputWrapper>
                {confirmPassword && confirmPasswordError && (
                  <Typography
                    id="confirm-password-error"
                    variant="caption"
                    color="error"
                    aria-live="polite"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {confirmPasswordError}
                  </Typography>
                )}
              </InputGroup>
            )}

            {mode === "signup" && (
              <RecaptchaContainer>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
                  onChange={(token: string | null) =>
                    setRecaptchaToken(token || "")
                  }
                />
              </RecaptchaContainer>
            )}

            {message && (
              <Message
                isError={message.toLowerCase().includes("failed")}
                role={message.toLowerCase().includes("failed") ? "alert" : "status"}
              >
                {message}
              </Message>
            )}

            {showResendLink && (
              <ToggleButton
                type="button"
                onClick={handleResendVerification}
                disabled={isResending}
              >
                {isResending ? "Sending..." : "Resend verification link"}
              </ToggleButton>
            )}

            {mode === "signup" && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  mb: 1.5,
                  px: 1,
                }}
              >
                By clicking Register, you agree to our{" "}
                <Box
                  component="a"
                  href="https://hellonoticeboard.com/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Terms
                </Box>
                {" and "}
                <Box
                  component="a"
                  href="https://hellonoticeboard.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Privacy Policy
                </Box>{" "}
               
                . You may receive SMS notifications from us and can opt out at any
                time.
              </Typography>
            )}

            <SubmitButton
              type="submit"
              disabled={
                mode === "signup" ? !recaptchaToken || isSigningUp : isLoggingIn
              }
              data-testid={mode === "signup" ? "signup-submit-button" : "login-submit-button"}
            >
              {mode === "signup"
                ? isSigningUp
                  ? "Registering..."
                  : "Register"
                : isLoggingIn
                ? "Signing in..."
                : (
                    <>
                      Login{" "}
                      <ArrowForwardIcon
                        sx={{ fontSize: "1.1rem", verticalAlign: "middle", ml: 0.5 }}
                      />
                    </>
                  )}
            </SubmitButton>

            {mode === "login" && (
              <OrDivider>
                <span>or</span>
              </OrDivider>
            )}

            <ToggleButton
              type="button"
              onClick={() => {
                if (mode === "signup") {
                  setMode("login");
                } else if (mode === "login") {
                  setMode("signup");
                }
                setMessage("");
              }}
              style={{ fontSize: "0.75rem" }}
              data-testid={mode === "signup" ? "signup-toggle-to-login" : "login-toggle-to-signup"}
            >
              {mode === "signup"
                ? "Already registered? Login"
                : (
                    <>
                      Need to register?{" "}
                      <SignUpHighlight>Sign up</SignUpHighlight>
                    </>
                  )}
            </ToggleButton>
          </form>
        </FormContainer>
      )}

      {/* Verification Form */}
      {mode === "verify" && (
        <FormContainer style={{ marginTop: "20px" }}>
          <FormTitle ref={headingRef} tabIndex={-1}>Verify Your Account</FormTitle>
          <form onSubmit={handleVerification}>
            <InputGroup>
              <Label htmlFor="verificationCode">Verification Code</Label>
              <Input
                id="verificationCode"
                type="text"
                placeholder="Enter 6-digit code from your email"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
                required
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                data-testid="verification-code-input"
              />
            </InputGroup>

            {message && (
              <Message
                isError={message.toLowerCase().includes("failed")}
                role={message.toLowerCase().includes("failed") ? "alert" : "status"}
              >
                {message}
              </Message>
            )}

            <ToggleButton
              type="button"
              onClick={handleResendVerification}
              disabled={isResending}
            >
              {isResending ? "Sending..." : "Resend verification code"}
            </ToggleButton>

            <SubmitButton type="submit" disabled={isVerifying} data-testid="verification-submit-button">
              {isVerifying ? "Verifying..." : "Verify Account"}
            </SubmitButton>

            <ToggleButton
              type="button"
              onClick={() => {
                setMode("login");
                setMessage("");
                setVerificationCode("");
                setSignupEmail("");
              }}
              data-testid="verification-back-to-login"
            >
              Back to Login
            </ToggleButton>
          </form>
        </FormContainer>
      )}

      <ForgotPasswordModal
        isOpen={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
      />
    </FormSection>
  );
};

export default SchoolSignupForm;
